import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { coreKeys, coreNav } from '@saas/module-core/ui';
import { calendarNav } from '@saas/module-calendar/ui';
import { campaignNav } from '@saas/module-campaign/ui';
import { chatNav } from '@saas/module-chat/ui';
import { companyProfileNav } from '@saas/module-company-profile/ui';
import { contractNav } from '@saas/module-contract/ui';
import { conversationApi, conversationKeys, conversationNav } from '@saas/module-conversation/ui';
import { crmNav } from '@saas/module-crm/ui';
import { expenseNav } from '@saas/module-expense/ui';
import { knowledgeNav } from '@saas/module-knowledge/ui';
import { meetingIngestNav } from '@saas/module-meeting-ingest/ui';
import { projectNav } from '@saas/module-project/ui';
import { invoiceNav } from '@saas/module-invoice/ui';
import { taskNav } from '@saas/module-task/ui';
import { assetNav } from '@saas/module-asset/ui';
import { relationshipKeys, relationshipNav } from '@saas/module-relationship/ui';
import { reportNav } from '@saas/module-report/ui';
import { safetyNav } from '@saas/module-safety/ui';
import { salesNav } from '@saas/module-sales/ui';
import { surveyNav } from '@saas/module-survey/ui';
import { wikiNav } from '@saas/module-wiki/ui';
import { storageNav } from '@saas/module-storage/ui';
import { telephonyNav } from '@saas/module-telephony/ui';
import { workflowKeys, workflowNav } from '@saas/module-workflow/ui';
import { useApi, useAuth, type ModuleNav, type NavItem } from '@saas/ui';

/**
 * メニューの集約。各モジュールの ui/index.ts が出す ModuleNav を、
 * 機能フラグ（enabledModules）と権限（can）で絞ってシェルに渡す。
 */
// 並びは 03 §12.10.2 の優先度（人 → 仕事の会話 → 顧客 → 案件 → 書類 → 知識）に合わせる
const MODULE_NAVS: ModuleNav[] = [
  coreNav,
  workflowNav,
  chatNav,
  conversationNav,
  crmNav,
  companyProfileNav,
  relationshipNav,
  salesNav,
  // SFA 段階 5: 一斉メール（§9.6）と電話連携（§9.4）
  campaignNav,
  telephonyNav,
  projectNav,
  invoiceNav,
  taskNav,
  assetNav,
  expenseNav,
  contractNav,
  reportNav,
  calendarNav,
  meetingIngestNav,
  wikiNav,
  storageNav,
  knowledgeNav,
  surveyNav,
  safetyNav,
];

export interface NavGroup {
  module: string;
  label: string;
  items: NavItem[];
}

export interface NavModel {
  /** 自分宛て・通知 */
  primary: NavItem[];
  /** 業務（モジュールごと） */
  work: NavGroup[];
  /** 管理 */
  admin: NavItem[];
  counts: Record<string, number>;
}

const MODULE_LABEL: Record<string, string> = {
  workflow: '申請・承認',
  expense: '経費精算',
  chat: 'チャット',
  conversation: '会話',
  crm: '取引先',
  'company-profile': '企業プロファイル',
  relationship: '関係（接点）',
  sales: '営業',
  contract: '契約書',
  project: '案件',
  invoice: '請求書',
  task: 'ToDo',
  report: '日報',
  calendar: 'カレンダー',
  'meeting-ingest': '会議',
  wiki: 'マニュアル',
  storage: 'ファイル',
  knowledge: 'ナレッジ',
  survey: 'アンケート・理解テスト',
  safety: '安否確認',
  campaign: '一斉メール',
  telephony: '電話連携',
};

export function useNavModel(): NavModel {
  const { can, isAdmin, isModuleEnabled, me } = useAuth();
  const api = useApi();
  const workflowOn = isModuleEnabled('workflow');
  const wikiBackend = useQuery({
    queryKey: ['wiki', 'backend'],
    queryFn: () => api.get<{ backend: 'native' | 'docmost' }>('/wiki/docmost/config'),
    enabled: isModuleEnabled('wiki') && can('wiki.page.read'),
  });

  const inbox = useQuery({
    queryKey: workflowKeys.inboxCount(),
    queryFn: () => api.get<{ count: number }>('/workflow/inbox/count'),
    enabled: workflowOn,
    refetchInterval: 60_000,
  });
  const unread = useQuery({
    queryKey: coreKeys.unreadCount(),
    queryFn: () => api.get<{ count: number }>('/notifications/unread-count'),
    refetchInterval: 60_000,
  });
  // レコード会話は「未読があるときだけ」サイドバーに出す（03 §12.10.2）。GET /conversation/me は軽い集計 1 本
  const conversationOn = isModuleEnabled('conversation') && can('conversation.message.read');
  const conversations = useQuery({
    queryKey: conversationKeys.me(),
    queryFn: () => conversationApi.me(api),
    enabled: conversationOn,
    refetchInterval: 60_000,
  });

  // 「会議のあとの 1 問」（03 §17.5 ②）。未確定の名寄せの件数
  const relationshipOn = isModuleEnabled('relationship') && can('relationship.profile.read');
  const unmatched = useQuery({
    queryKey: relationshipKeys.unmatchedCount(),
    queryFn: () => api.get<{ count: number }>('/relationship/unmatched/count'),
    enabled: relationshipOn,
    refetchInterval: 5 * 60_000,
  });
  // 見込み客の未対応（SFA 1A・設計書 §4）。crm の一覧と同じ絞り込みで件数だけ取る
  const leadsOn = isModuleEnabled('crm') && isModuleEnabled('sales') && can('crm.lead.read');
  const untouchedLeads = useQuery({
    queryKey: ['crm', 'leads', 'count', 'untouched'],
    queryFn: () => api.get<{ count: number }>('/crm/leads/count', { untouched: '1' }),
    enabled: leadsOn,
    refetchInterval: 5 * 60_000,
  });
  const chatOn = isModuleEnabled('chat');

  return useMemo(() => {
    const allowed = (it: NavItem): boolean => {
      if (it.roles && it.roles.some((r) => me?.roles.includes(r))) return true;
      if (it.permission) {
        const list = Array.isArray(it.permission) ? it.permission : [it.permission];
        if (list.some((p) => can(p))) return true;
        return it.roles ? false : isAdmin && list.length === 0;
      }
      return !it.roles;
    };
    const primary: NavItem[] = [];
    const work: NavGroup[] = [];
    const admin: NavItem[] = [];
    for (const nav of MODULE_NAVS) {
      if (!isModuleEnabled(nav.module)) continue;
      const items = nav.items.filter(allowed).filter((item) => nav.module !== 'wiki' || wikiBackend.data?.backend !== 'docmost' || item.to === '/manual');
      if (nav.module === 'core') primary.push(...items);
      else if (items.length) work.push({ module: nav.module, label: MODULE_LABEL[nav.module] ?? nav.module, items });
      admin.push(...nav.admin.filter(allowed));
    }
    return {
      primary,
      work,
      admin,
      counts: {
        'workflow.inbox': inbox.data?.count ?? 0,
        'notifications.unread': unread.data?.count ?? 0,
        // バッジの式（§12.10.6）: チャット = 未読 DM + 自分宛てメンション。chat が有効ならメンションはチャット側に寄せ、「会話」は未読のレコード会話の数だけにする
        'chat.unread': conversations.data ? conversations.data.unreadDm + conversations.data.mentions : 0,
        'conversation.unread': conversations.data ? conversations.data.unreadRecordConversations.length + (chatOn ? 0 : conversations.data.mentions) : 0,
        'relationship.unmatched': unmatched.data?.count ?? 0,
        'sales.leads.untouched': untouchedLeads.data?.count ?? 0,
      },
    };
  }, [can, isAdmin, isModuleEnabled, me, inbox.data, unread.data, conversations.data, unmatched.data, untouchedLeads.data, chatOn, wikiBackend.data]);
}
